import * as mammoth from 'mammoth';
import { supabase } from '@/integrations/supabase/client';

interface ExtractionResult {
  success: boolean;
  text?: string;
  error?: string;
}

/**
 * Extract text from various file types
 */
export class FileTextExtractor {
  /**
   * Extract text from a file based on its type
   */
  static async extractText(file: File): Promise<ExtractionResult> {
    try {
      const fileType = file.type.toLowerCase();
      
      // Handle different file types
      if (fileType === 'application/pdf') {
        return await this.extractFromPDF(file);
      } else if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        return await this.extractFromDOCX(file);
      } else if (fileType === 'application/msword') {
        // For legacy .doc files, we'll show a helpful message
        return {
          success: false,
          error: 'Legacy .doc files are not supported. Please save your document as .docx or .pdf format.'
        };
      } else if (fileType === 'text/plain') {
        return await this.extractFromTXT(file);
      } else {
        return {
          success: false,
          error: 'Unsupported file type. Please upload a PDF, DOCX, or TXT file.'
        };
      }
    } catch (error) {
      console.error('Error extracting text from file:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to extract text from file'
      };
    }
  }

  private static async extractFromPDF(file: File): Promise<ExtractionResult> {
    try {
      console.log('Starting PDF extraction for file:', file.name);
      
      // First upload the file to get a URL for PDF.co processing
      const fileName = `temp-pdf-${Date.now()}-${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('uploaded-documents')
        .upload(fileName, file);

      if (uploadError) {
        console.error('Upload failed:', uploadError);
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('uploaded-documents')
        .getPublicUrl(fileName);

      console.log('Calling convert-pdf-to-text function with PDF.co API...');
      const { data, error } = await supabase.functions.invoke('convert-pdf-to-text', {
        body: { fileUrl: publicUrl }
      });
      
      console.log('PDF.co function response:', { success: data?.success, hasText: !!data?.text, error });
      
      // Clean up the uploaded file
      try {
        await supabase.storage
          .from('uploaded-documents')
          .remove([fileName]);
      } catch (cleanupError) {
        console.warn('Failed to clean up temporary file:', cleanupError);
      }
      
      if (error) {
        console.error('PDF.co function error:', error);
        throw error;
      }

      if (!data?.success || !data?.text) {
        throw new Error(data?.error || 'PDF.co extraction failed');
      }

      // Validate extracted text
      if (data.text.trim().length < 20) {
        return {
          success: false,
          error: 'PDF appears to contain very little text. Please use the "Paste Text" option instead.'
        };
      }

      console.log('PDF extraction successful via PDF.co, text length:', data.text.length);
      return {
        success: true,
        text: data.text.trim()
      };
      
    } catch (error) {
      console.error('PDF extraction error:', error);
      return {
        success: false,
        error: 'Failed to extract text from PDF using PDF.co. Please use the "Paste Text" option to copy and paste your resume content instead.'
      };
    }
  }

  /**
   * Extract text from DOCX using mammoth
   */
  private static async extractFromDOCX(file: File): Promise<ExtractionResult> {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      
      if (result.value && result.value.trim().length > 20) {
        return {
          success: true,
          text: result.value.trim()
        };
      } else {
        return {
          success: false,
          error: 'Word document appears to be empty. Please check the file and try again.'
        };
      }
    } catch (error) {
      console.error('DOCX extraction error:', error);
      return {
        success: false,
        error: 'Failed to extract text from Word document. Please save as PDF or copy the text manually.'
      };
    }
  }

  /**
   * Extract text from TXT files
   */
  private static async extractFromTXT(file: File): Promise<ExtractionResult> {
    try {
      const text = await file.text();
      
      if (text && text.trim().length > 20) {
        return {
          success: true,
          text: text.trim()
        };
      } else {
        return {
          success: false,
          error: 'Text file appears to be empty.'
        };
      }
    } catch (error) {
      console.error('TXT extraction error:', error);
      return {
        success: false,
        error: 'Failed to read text file.'
      };
    }
  }

  /**
   * Validate file before processing
   */
  static validateFile(file: File): { valid: boolean; error?: string } {
    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      return {
        valid: false,
        error: 'File size too large. Please upload a file smaller than 10MB.'
      };
    }
    
    // Check file type
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Unsupported file type. Please upload a PDF, DOCX, or TXT file.'
      };
    }
    
    return { valid: true };
  }
}